import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * A rotating quote panel. Fetches curated quotes from /api/quotes and cycles
 * every ~25 seconds with a soft fade. Displays Latin (if available) beneath
 * the English translation for scripture and classical quotes.
 */
const QuoteRotator = ({ intervalMs = 25000 }) => {
    const [quotes, setQuotes] = useState([]);
    const [idx, setIdx] = useState(0);
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        api.get("/quotes").then(r => {
            const qs = r.data?.quotes || [];
            // Shuffle once so every visitor's first quote is different
            const shuffled = [...qs].sort(() => Math.random() - 0.5);
            setQuotes(shuffled);
        });
    }, []);

    useEffect(() => {
        if (quotes.length < 2) return;
        const tick = setInterval(() => {
            setVisible(false);
            setTimeout(() => {
                setIdx(i => (i + 1) % quotes.length);
                setVisible(true);
            }, 700);  // fade duration
        }, intervalMs);
        return () => clearInterval(tick);
    }, [quotes, intervalMs]);

    if (!quotes.length) return null;
    const q = quotes[idx];

    return (
        <div className="max-w-3xl mx-auto px-6 py-2" data-testid="quote-rotator">
            <div
                className={`transition-opacity duration-700 ease-in-out ${visible ? "opacity-100" : "opacity-0"}`}
                style={{ minHeight: "160px" }}
            >
                <div className="text-center">
                    <div className="text-5xl accent-liturgy font-serif leading-none mb-2" aria-hidden>&ldquo;</div>
                    <p className="font-serif text-xl md:text-2xl leading-relaxed text-foreground/90 italic" data-testid="quote-text">
                        {q.text.en}
                    </p>
                    {q.text.la && q.text.la !== q.text.en && (
                        <p className="mt-3 font-serif text-sm md:text-base text-muted-foreground italic tracking-wide" data-testid="quote-latin">
                            {q.text.la}
                        </p>
                    )}
                    <div className="mt-5 small-caps text-[11px] text-muted-foreground tracking-wider">
                        — <span className="accent-liturgy" data-testid="quote-author">{q.author}</span>
                        {q.source && (
                            <span className="text-muted-foreground/70"> · {q.source}</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuoteRotator;
